/**
 * Toasts.
 *
 * The requirements that shape this, in the order they matter:
 *
 * **Announced.** A confirmation only a sighted user sees is not a confirmation. The region is a
 * live region; `error` uses `assertive` because a failed publish must interrupt, everything else
 * uses `polite` so a success does not cut across what someone is reading.
 *
 * **Queued, not stacked forever.** Three visible at once, the rest wait. A back office that fires
 * eight toasts during a bulk upload otherwise covers the screen it is reporting on.
 *
 * **Auto-dismiss with a manual close, and errors do not auto-dismiss.** A success can vanish; an
 * error the user has not read yet must not, because the whole reason it exists is that something
 * needs their attention.
 *
 * Hovering pauses the timer — the common case for a toast still on screen is that someone is
 * reading it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cx } from "./cx";

export type ToastTone = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /** Optional detail: the server's reason, when there is one worth showing. */
  detail?: string;
}

/** How many are visible at once. The rest queue. */
export const MAX_VISIBLE = 3;

/** Auto-dismiss delay. Errors ignore it — see the module docstring. */
export const DISMISS_MS = 6000;

interface ToastContextValue {
  show: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside a ToastProvider");
  }
  return context;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Accessible name for the region, localized by the caller. */
  regionLabel: string;
  closeLabel: string;
}

export function ToastProvider({
  children,
  regionLabel,
  closeLabel,
}: ToastProviderProps): JSX.Element {
  const [queue, setQueue] = useState<Toast[]>([]);
  const counter = useRef(0);

  const show = useCallback((toast: Omit<Toast, "id">) => {
    counter.current += 1;
    setQueue((current) => [...current, { ...toast, id: `toast-${counter.current}` }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setQueue((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const visible = queue.slice(0, MAX_VISIBLE);
  const value = useMemo(
    () => ({ show, dismiss, toasts: queue }),
    [show, dismiss, queue],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-label={regionLabel}
        className="pointer-events-none fixed bottom-4 end-4 z-toast flex w-full max-w-panel flex-col gap-2"
      >
        {visible.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onDismiss={dismiss}
            closeLabel={closeLabel}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONES: Record<ToastTone, string> = {
  success: "border-statusSuccess bg-statusSuccessSubtle text-textPrimary",
  error: "border-statusDanger bg-statusDangerSubtle text-textPrimary",
  warning: "border-statusWarning bg-statusWarningSubtle text-textPrimary",
  info: "border-statusInfo bg-statusInfoSubtle text-textPrimary",
};

function ToastItem({
  toast,
  onDismiss,
  closeLabel,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
  closeLabel: string;
}): JSX.Element {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // An error stays until dismissed. Everything else clears itself.
    if (toast.tone === "error" || paused) return;
    const timer = setTimeout(() => onDismiss(toast.id), DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toast.id, toast.tone, paused, onDismiss]);

  return (
    <div
      role={toast.tone === "error" ? "alert" : "status"}
      aria-live={toast.tone === "error" ? "assertive" : "polite"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cx(
        "pointer-events-auto flex items-start gap-3 rounded-md border p-3 shadow-toast",
        TONES[toast.tone],
      )}
    >
      <div className="flex-1">
        <p className="text-body-md">{toast.message}</p>
        {toast.detail ? (
          <p className="mt-1 text-label-md text-textMuted">{toast.detail}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={closeLabel}
        className="rounded-sm px-1 text-textMuted hover:bg-surfaceRaised focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}
